import React from 'react'
import classNames from 'classnames'
import PerfectScrollbar from 'react-perfect-scrollbar'

// evertel
import { Badge, FormInput, Icon, Row, Text, useRovingTabIndex } from '@evertel/web/ui'

interface BrowseCategory {
    key: string
    label: string
    count: number
}

interface CategoryListProps {
    categories: BrowseCategory[]
    selectedKey: string
    onSelect: (key: string) => void
    searchValue: string
    onSearchChange: (value: string) => void
    // id of the panel (TemplateGrid) whose contents the selected category drives
    panelId: string
}

// DOM id of a category's tab; TemplateGrid points aria-labelledby at the selected one
const categoryTabId = (key: string) => `template-category-tab-${key}`

const CategoryList: React.FC<CategoryListProps> = ({
    categories,
    selectedKey,
    onSelect,
    searchValue,
    onSearchChange,
    panelId
}) => {

    // vertical tablist with automatic activation: the selected tab is the single Tab stop,
    // Up/Down/Home/End move focus and select in one step, Tab moves on to the template grid
    const { containerRef, onKeyDown: onListKeyDown } = useRovingTabIndex({
        itemSelector: '[role="tab"]',
        orientation: 'vertical',
        onFocusItem: (item) => {
            const key = item.dataset.categoryKey
            if (key) onSelect(key)
        }
    })

    const onTabKeyDown = (e: React.KeyboardEvent, key: string) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onSelect(key)
            return
        }
        onListKeyDown(e)
    }

    return (
        <div className="template-browse-panel">
            <div className="position-relative">
                <FormInput
                    type="text"
                    name="findTemplate"
                    label="Find a template..."
                    placeholder="Find a template..."
                    value={searchValue}
                    onChange={(e) => onSearchChange(e.target.value)}
                    aria-label="Find a template"
                    className="mb-3"
                />
                {searchValue && (
                    <button
                        type="button"
                        onClick={() => onSearchChange('')}
                        className="btn btn-close position-absolute"
                        style={{
                            right: 0,
                            bottom: 19,
                            zIndex: 10
                        }}
                        aria-label="Clear filter"
                    />
                )}
            </div>
            <PerfectScrollbar
                options={{ suppressScrollX: true }}
                className="flex-grow-1"
                style={{ minHeight: 0 }}>
                <div
                    ref={containerRef}
                    role="tablist"
                    aria-label="Template categories"
                    aria-orientation="vertical">
                    {categories.map((category) => {
                        const isSelected = category.key === selectedKey

                        return (
                            <Row
                                key={category.key}
                                id={categoryTabId(category.key)}
                                role="tab"
                                aria-selected={isSelected}
                                aria-controls={panelId}
                                tabIndex={isSelected ? 0 : -1}
                                data-category-key={category.key}
                                align="between"
                                valign="center"
                                className={classNames(
                                    'category-list-item',
                                    { selected: isSelected }
                                )}
                                onClick={() => onSelect(category.key)}
                                onKeyDown={(e) => onTabKeyDown(e, category.key)}>
                                <Row valign="center">
                                    <Text
                                        bold
                                        color={isSelected ? 'primary' : undefined}>
                                        {category.label}
                                    </Text>
                                    <Badge
                                        color="primary"
                                        className="ms-2">
                                        {category.count}
                                    </Badge>
                                </Row>
                                <Icon
                                    name="chevron-right"
                                    size={12}
                                    color="muted"
                                />
                            </Row>
                        )
                    })}
                </div>
            </PerfectScrollbar>
        </div>
    )
}

export { CategoryList, categoryTabId }
export type { BrowseCategory }
